import { Router, ActivatedRoute } from '@angular/router';
import { RestService } from './../../services/rest.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { BackButtonService } from 'src/app/services/back-button.service';
import { AccordionService } from 'src/app/services/accordion.service';
import { ProcessingSheetData } from './processing-sheet.model';
import { Subject, takeUntil } from 'rxjs';
import { ViewWillEnter } from '@ionic/angular';
import { FormControl, Validators } from '@angular/forms';
import { SnackbarService } from '@bcl/design-system';

const ACCORDION_GROUP_ID = 'processing-sheet';

interface SectionConfig {
  id: string;
  title: string;
}

@Component({
  selector: 'app-processing-sheet',
  templateUrl: './processing-sheet.component.html',
  styleUrls: ['./processing-sheet.component.scss'],
})
export class ProcessingSheetComponent
  implements OnInit, OnDestroy, ViewWillEnter
{
  public orderId: number;
  public processingLine: string | null = null;
  public order: any = null;
  public loading = false;
  public processingData: ProcessingSheetData | null = null;

  // Accordion sections configuration
  public sections: SectionConfig[] = [
    { id: 'order', title: 'Order' },
    { id: 'inputProduct', title: 'Input Product Details' },
    { id: 'amount', title: 'Amount' },
    { id: 'outputProduct', title: 'Output Product Details' },
    { id: 'cleaningWaste', title: 'Cleaning & Waste Handling' },
    { id: 'machineSettings', title: 'Machine Settings' },
    { id: 'blockages', title: 'Blockages and Deviations' },
    { id: 'remarks', title: 'Remarks' },
  ];

  // Track open state for each section
  public sectionStates: Map<string, boolean> = new Map();

  // Form control for blocked remarks
  public blockedRemarksControl = new FormControl('', [Validators.required]);

  private destroyed$ = new Subject<void>();

  constructor(
    private rest: RestService,
    private router: Router,
    private route: ActivatedRoute,
    private backButtonService: BackButtonService,
    private accordionService: AccordionService,
    private snackbarService: SnackbarService
  ) {
    this.orderId = +this.route.snapshot.paramMap.get('id');
    this.processingLine = this.route.snapshot.queryParamMap.get('line');
  }

  ngOnInit() {
    // Register all sections with the accordion service
    this.sections.forEach((section, index) => {
      const sectionId = this.getSectionId(section.id);
      // First section opens by default
      const initialState = index === 0;

      this.accordionService
        .registerItem(sectionId, ACCORDION_GROUP_ID, initialState)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((item) => {
          this.sectionStates.set(section.id, item.isOpen);
        });
    });

    this.getData();
  }

  ngOnDestroy(): void {
    // Unregister all sections from the accordion service
    this.sections.forEach((section) => {
      this.accordionService.unregisterItem(this.getSectionId(section.id));
    });

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ionViewWillEnter(): void {
    this.setupBackButton();
  }

  getData() {
    this.loading = true;
    this.rest.getOrderProcessingSheet(this.orderId).then((order: any) => {
      if (order && order.length > 0) {
        this.order = order[0];
        this.mapOrderToProcessingData(this.order);
      }
      this.loading = false;
    });
  }

  toggleSection(sectionId: string): void {
    this.accordionService.toggleItem(this.getSectionId(sectionId));
  }

  isSectionOpen(sectionId: string): boolean {
    return this.sectionStates.get(sectionId) || false;
  }

  goBack() {
    this.router.navigate(['']);
  }

  goToProcessingDetails() {
    this.router.navigate(['processing-details', this.orderId], {
      queryParams: { line: this.processingLine },
    });
  }

  // Block order with remarks
  async blockOrder(): Promise<void> {
    if (!this.processingData || this.blockedRemarksControl.invalid) {
      return;
    }

    try {
      await this.rest.blockOrder(
        this.orderId,
        true,
        this.blockedRemarksControl.value || ''
      );
      // Update locally and navigate back
      this.processingData.blocked = true;
      this.snackbarService.open(
        'green',
        'PROCESSING_SHEET.MESSAGES.ORDER_BLOCKED'
      );
      this.goBack();
    } catch (error) {
      console.error('Error blocking order:', error);
      this.snackbarService.open(
        'red',
        'PROCESSING_SHEET.MESSAGES.ORDER_BLOCK_ERROR'
      );
    }
  }

  private setupBackButton(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  private getSectionId(sectionId: string): string {
    return `${ACCORDION_GROUP_ID}-${sectionId}`;
  }

  private mapOrderToProcessingData(order: any): void {
    // Map the order data to our processing sheet structure
    // This mapping should be adjusted based on actual API response
    this.processingData = {
      order: {
        orderNumber: order.orderNumber || '',
        outputAt: order.outputNumber || '',
        productionLine: this.processingLine || '',
        organic: order.bio ? 'Yes' : 'No',
      },
      inputProduct: {
        customerNumber: order.customer || '',
        productType: order.commodity || '',
        lotNumber: order.lotNumber || '',
        documentNumber: order.documentNumber || '',
      },
      amount: {
        inputNumber: order.inputNumber || '',
        numberOfPackages: order.colli ? `${order.colli} ` : '',
        grossWeight: order.weight ? `${order.weight} kg` : '',
        netWeight: order.nett ? `${order.nett} kg` : '',
      },
      outputProduct: {
        outputNumber: order.outputNumber || '',
        productType: order.outputProductType || '',
        typeOfPackage: order.packageType || '',
        paletteType: order.paletteType || '',
        weight: order.outputWeight ? `${order.outputWeight} kg` : '',
      },
      cleaningWaste: {
        reasonForCleaning: order.cleaningReason || '',
        wasteHandling: order.wasteHandling || '',
        cleaningWasteRemarks: order.cleaningReasonRemarks || '',
        wasteHandlingRemarks: order.wasteHandlingRemarks || '',
      },
      machineSettings: {
        xray: order.xray || '',
        laserProgram: order.laserProgram || '',
        sieveSize: order.sieveSize || '',
        aspirator: order.aspirator || '',
      },
      blocked: order.blocked || false,
      blockedRemarks: order.blockedRemarks || '',
      remarks: order.remarks || '',
      bio: order.bio || false,
      warrant: order.warrant || '',
      date: order.date || '',
    };

    // Initialize form control with existing value
    this.blockedRemarksControl.setValue(order.blockedRemarks || '');
  }
}
